/* eslint-disable @typescript-eslint/no-unused-expressions */
import { useCallback, useEffect, useMemo, useState, useTransition } from 'react'
import { useDebounce } from '@/hooks/useDebounce'

type Opts = { initialPageSize?: number; debounceMs?: number }

export function useServerTableControls({
  initialPageSize = 20,
  debounceMs = 400,
}: Opts = {}) {
  const [search, setSearch] = useState('')
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const debouncedSearch = useDebounce(search, debounceMs)

  const [, startTransition] = useTransition()
  const onSearchChange = useCallback((v: string) => {
    startTransition(() => setSearch(v))
  }, [])

  const onPageChange = useCallback((i: number) => setPageIndex(i), [])
  const onPageSizeChange = useCallback((n: number) => {
    setPageSize(n), setPageIndex(0)
  }, [])

  // Quand la recherche change, on revient à la page 1 (index 0)
  useEffect(() => {
    setPageIndex(0)
  }, [debouncedSearch])

  const tableProps = useMemo(
    () => ({
      searchValue: search,
      onSearchChange, // stable
      currentPageIndex: pageIndex,
      currentPageSize: pageSize,
      onPageChange, // stable
      onPageSizeChange, // stable
      paginationServer: true as const,
      sortingServer: false as const,
    }),
    [
      search,
      pageIndex,
      pageSize,
      onSearchChange,
      onPageChange,
      onPageSizeChange,
    ]
  )

  return {
    search,
    setSearch: onSearchChange,
    debouncedSearch,
    pageIndex,
    setPageIndex: onPageChange,
    pageSize,
    setPageSize: onPageSizeChange,
    tableProps,
  }
}
